import { useEffect, useRef } from 'react';

let listboxCounter = 0;

/** Stable per-mount id (React 17 has no `useId`). */
export function useMentionListboxId(prefix = 'mention-listbox'): string {
  const ref = useRef<string>();
  if (!ref.current) {
    listboxCounter += 1;
    ref.current = `${prefix}-${listboxCounter}`;
  }
  return ref.current;
}

/** Set `attributes` on `el`; the returned cleanup restores what was there. */
function applyAttributes(
  el: HTMLElement,
  attributes: Record<string, string>
): () => void {
  const previous = Object.keys(attributes).map(
    (name) => [name, el.getAttribute(name)] as const
  );
  Object.entries(attributes).forEach(([name, value]) =>
    el.setAttribute(name, value)
  );
  return () => {
    previous.forEach(([name, value]) => {
      if (value === null) el.removeAttribute(name);
      else el.setAttribute(name, value);
    });
  };
}

/**
 * ARIA 1.2 editable-combobox state for an editor that keeps focus while its
 * mention list is open. Without it a screen reader announces neither the open
 * list nor the row the arrow keys land on. Mirrors kai's MentionSuggestions.
 *
 * `role`/`aria-expanded`/`aria-controls` are applied once per open list;
 * only `aria-activedescendant` follows the highlighted row, so assistive tech
 * does not re-announce the whole control on every arrow key.
 */
export function useMentionComboboxAria(
  getEditable: () => HTMLElement | null | undefined,
  isOpen: boolean,
  listboxId: string,
  activeOptionId: string | undefined
): void {
  const getEditableRef = useRef(getEditable);
  getEditableRef.current = getEditable;

  useEffect(() => {
    const el = getEditableRef.current();
    if (!el || !isOpen) return undefined;
    return applyAttributes(el, {
      role: 'combobox',
      'aria-expanded': 'true',
      'aria-controls': listboxId,
      'aria-haspopup': 'listbox',
    });
  }, [isOpen, listboxId]);

  useEffect(() => {
    const el = getEditableRef.current();
    if (!el || !isOpen || !activeOptionId) return undefined;
    return applyAttributes(el, { 'aria-activedescendant': activeOptionId });
  }, [isOpen, activeOptionId]);
}
